import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useQueryClient } from '@tanstack/react-query';
import { Button } from '@/components/ui/button';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogTrigger,
} from '@/components/ui/alert-dialog';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { 
  AlertTriangle, 
  CreditCard, 
  Sparkles, 
  ArrowRight, 
  Clock,
  Receipt,
  ShieldAlert,
  LogOut
} from 'lucide-react';
import { accountService, DelinquentCharge, UserSubscription } from '@/services/account.service';
import type { TerminalSubscription } from '@/services/user.service';
import { format, parseISO } from 'date-fns';
import { ptBR } from 'date-fns/locale';
import { useAuth } from '@/contexts/auth.context';
import { usePublicPlanNames } from '@/hooks/use-public-plans';
import { openInNewTab } from '@/lib/open-in-new-tab';
import { formatBrasilia } from '@/lib/brasiliaDate';

interface SubscriptionBlockedPageProps {
  blockReason: 'no_subscription' | 'payment_overdue' | 'trial_expired' | 'subscription_cancelled';
  daysOverdue?: number;
  overdueInvoice?: DelinquentCharge | null;
  subscription?: UserSubscription | null;
  terminalSubscription?: TerminalSubscription | null;
}

const AVISO_PERDA_DE_ACESSO_PAGO =
  'Você perderá o acesso às funcionalidades pagas do seu plano atual. ' +
  'Poderá voltar a assinar um plano pago quando quiser.';

export function SubscriptionBlockedPage({
  blockReason,
  daysOverdue = 0,
  overdueInvoice,
  terminalSubscription,
}: Readonly<SubscriptionBlockedPageProps>) {
  const navigate = useNavigate();
  const { logout } = useAuth();
  const queryClient = useQueryClient();
  const [downgrading, setDowngrading] = useState(false);
  const [downgradeError, setDowngradeError] = useState<string | null>(null);
  // Nomes reais dos planos públicos (de /public/plans) — nunca sugerir
  // planos fixos no código.
  const publicPlanNames = usePublicPlanNames();

  const handleDowngradeToFree = async () => {
    setDowngrading(true);
    setDowngradeError(null);
    try {
      await accountService.downgradeToFree();
      // Após o downgrade o usuário entra no plano gratuito (sem trial). Revalida
      // o estado e recarrega para liberar o acesso (guard + features).
      await queryClient.invalidateQueries({ queryKey: ['my-subscription'] });
      window.location.reload();
    } catch (error) {
      // Preserva a mensagem real do backend: engolir o erro aqui foi o que fez
      // este defeito chegar ao suporte com diagnóstico errado.
      const detail = (error as { response?: { data?: { detail?: string } } })?.response?.data
        ?.detail;
      setDowngrading(false);
      setDowngradeError(
        detail || 'Não foi possível migrar para o plano gratuito. Tente novamente.',
      );
    }
  };

  // Saída para o plano gratuito, sempre atrás de confirmação. Usada nos dois
  // ramos que a oferecem (inadimplência e fim de trial) para não duplicar a
  // regra nem divergir a cópia.
  const renderSaidaParaGratuito = (rotulo: string, descricao: string) => (
    <>
      <AlertDialog>
        <AlertDialogTrigger asChild>
          <Button variant="outline" className="w-full py-5" disabled={downgrading}>
            {downgrading ? 'Migrando para o plano gratuito...' : rotulo}
          </Button>
        </AlertDialogTrigger>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Migrar para o plano gratuito?</AlertDialogTitle>
            <AlertDialogDescription>{descricao}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Voltar</AlertDialogCancel>
            <AlertDialogAction onClick={handleDowngradeToFree}>Confirmar</AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
      {downgradeError && (
        <p className="text-sm text-red-600 text-center">{downgradeError}</p>
      )}
    </>
  );

  const isNoSubscription = blockReason === 'no_subscription';
  const isPaymentOverdue = blockReason === 'payment_overdue';
  const isTrialExpired = blockReason === 'trial_expired';
  const isCancelled = blockReason === 'subscription_cancelled';
  // `SubscriptionStatus` é um `str, Enum` cujos valores são minúsculos
  // ("cancelled", "inactive"), e este arquivo já compara status com
  // toLowerCase(). Comparar com maiúsculo aqui nunca casaria.
  const isInactiveStatus = terminalSubscription?.status?.toLowerCase() === 'inactive';

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted flex items-center justify-center p-4">
      <div className="max-w-lg w-full space-y-6">

        {/* Main Card */}
        <Card className="border-2 border-amber-200 dark:border-amber-800 shadow-xl">
          <CardHeader className="text-center pb-4">
            <div className={`w-16 h-16 rounded-full mx-auto mb-4 flex items-center justify-center ${
              isPaymentOverdue ? 'bg-red-100 dark:bg-red-900/30' : 'bg-amber-100 dark:bg-amber-900/30'
            }`}>
              {isPaymentOverdue ? (
                <ShieldAlert className="h-8 w-8 text-red-600 dark:text-red-400" />
              ) : (
                <AlertTriangle className="h-8 w-8 text-amber-600 dark:text-amber-400" />
              )}
            </div>

            <CardTitle className="text-2xl font-bold text-foreground">
              {isCancelled
                ? (isInactiveStatus ? 'Sua assinatura está inativa' : 'Sua assinatura foi cancelada')
                : isPaymentOverdue
                  ? 'Pagamento Pendente'
                  : isTrialExpired
                    ? 'Seu período grátis terminou'
                    : 'Assinatura Necessária'}
            </CardTitle>

            <CardDescription className="text-base text-muted-foreground mt-2">
              {isCancelled
                ? 'Para voltar a usar o Librattum, escolha um plano — ou siga gratuitamente no plano gratuito.'
                : isPaymentOverdue
                  ? `Sua fatura está vencida há ${daysOverdue} dias. Regularize seu pagamento para continuar usando o sistema.`
                  : isTrialExpired
                    ? 'Seu período de teste gratuito foi encerrado. Para continuar com os recursos premium, escolha um plano — ou siga gratuitamente no plano gratuito.'
                    : 'Para continuar usando o Librattum, você precisa selecionar um plano de assinatura.'}
            </CardDescription>
          </CardHeader>

          <CardContent className="space-y-6">
            {/* Overdue Invoice Details */}
            {isPaymentOverdue && overdueInvoice && (
              <div className="bg-red-50 dark:bg-red-900/20 rounded-xl p-4 border border-red-100 dark:border-red-800">
                <div className="flex items-start gap-3">
                  <Receipt className="h-5 w-5 text-red-600 dark:text-red-400 mt-0.5" />
                  <div className="flex-1">
                    <div className="flex items-center justify-between mb-2">
                      <span className="font-medium text-foreground">
                        Fatura #{overdueInvoice.invoice_number}
                      </span>
                      <Badge variant="destructive" className="text-xs">
                        <Clock className="h-3 w-3 mr-1" />
                        {daysOverdue} dias em atraso
                      </Badge>
                    </div>
                    <div className="text-sm text-muted-foreground space-y-1">
                      <div className="flex justify-between">
                        <span>Valor:</span>
                        <span className="font-semibold text-red-600 dark:text-red-400">
                          {formatCurrency(overdueInvoice.amount)}
                        </span>
                      </div>
                      <div className="flex justify-between">
                        <span>Vencimento:</span>
                        <span>
                          {format(parseISO(overdueInvoice.due_date), "dd 'de' MMMM 'de' yyyy", { locale: ptBR })}
                        </span>
                      </div>
                      {overdueInvoice.plan_name && (
                        <div className="flex justify-between">
                          <span>Plano:</span>
                          <span>{overdueInvoice.plan_name}</span>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            )}

            {/* Trial Expired Info */}
            {isTrialExpired && (
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-100 dark:border-amber-800">
                <div className="flex items-start gap-3">
                  <Clock className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground mb-1">
                      Continue aproveitando o Librattum
                    </p>
                    <p className="text-sm text-muted-foreground">
                      Escolha um plano para manter os recursos premium, ou continue
                      gratuitamente no plano gratuito do Librattum.
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* No Subscription Info */}
            {isNoSubscription && (
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-100 dark:border-amber-800">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div>
                    <p className="font-medium text-foreground mb-1">
                      Comece a organizar suas finanças
                    </p>
                    <p className="text-sm text-muted-foreground">
                      {publicPlanNames.length > 0
                        ? `Escolha o plano que se adapta às suas necessidades: ${publicPlanNames.join(', ')}.`
                        : 'Escolha o plano que se adapta às suas necessidades.'}
                    </p>
                  </div>
                </div>
              </div>
            )}

            {/* Estado terminal: contexto do encerramento, SEM dados de fatura.
                A cobrança antiga não é oferecida de propósito — a assinatura
                correspondente na VIO Pay também está cancelada, então pagá-la
                não reativaria o acesso. */}
            {isCancelled && (
              <div className="bg-amber-50 dark:bg-amber-900/20 rounded-xl p-4 border border-amber-100 dark:border-amber-800">
                <div className="flex items-start gap-3">
                  <Sparkles className="h-5 w-5 text-amber-600 dark:text-amber-400 mt-0.5" />
                  <div className="text-sm text-muted-foreground space-y-1">
                    {terminalSubscription?.plan_name && (
                      <p className="font-medium text-foreground">
                        {terminalSubscription.plan_name}
                      </p>
                    )}
                    {terminalSubscription?.cancelled_at && (
                      <p>
                        Encerrada em{' '}
                        {formatBrasilia(
                          terminalSubscription.cancelled_at,
                          "dd 'de' MMMM 'de' yyyy",
                        )}
                      </p>
                    )}
                    {terminalSubscription?.cancel_reason && (
                      <p>{terminalSubscription.cancel_reason}</p>
                    )}
                    <p>Seus dados financeiros foram preservados.</p>
                  </div>
                </div>
              </div>
            )}

            {/* Action Buttons */}
            <div className="space-y-3">
              {(() => {
                if (isCancelled) return (
                    <>
                      <Button
                        className="w-full py-6 text-base font-semibold"
                        // Propositalmente diferente de /account?tab=plan (usado pelos
                        // outros ramos abaixo): change-plan devolve 404 para uma
                        // assinatura terminal, então essa rota seria um beco sem saída
                        // aqui. /register/plans atende usuário autenticado e leva ao
                        // checkout de uma assinatura NOVA — não uma troca. Não mude
                        // isto para /account?tab=plan achando que é uma divergência.
                        onClick={() => navigate('/register/plans')}
                      >
                        <Sparkles className="h-5 w-5 mr-2" />
                        Escolher um plano
                        <ArrowRight className="h-5 w-5 ml-2" />
                      </Button>
                      {renderSaidaParaGratuito(
                        'Continuar no plano gratuito',
                        'Você entrará no plano gratuito e volta a usar o sistema com os recursos dele. Poderá assinar um plano pago quando quiser.',
                      )}
                    </>
                );
                if (isPaymentOverdue) return (
                    <>
                      <Button
                        className="w-full py-6 text-base font-semibold bg-red-600 hover:bg-red-700"
                        onClick={() => {
                          // Abre o checkout VIO Pay em nova aba para preservar a
                          // sessão do app. Fallback para /account?tab=billing se o
                          // backend não tiver enviado payment_url (caso degenerado).
                          if (overdueInvoice?.payment_url) {
                            openInNewTab(overdueInvoice.payment_url);
                            return;
                          }
                          navigate('/account?tab=billing');
                        }}
                      >
                        <CreditCard className="h-5 w-5 mr-2" />
                        Pagar Fatura Agora
                        <ArrowRight className="h-5 w-5 ml-2" />
                      </Button>
                      <Button
                        variant="outline"
                        className="w-full py-5"
                        onClick={() => navigate('/account?tab=plan')}
                      >
                        <Sparkles className="h-4 w-4 mr-2" />
                        Alterar Plano
                      </Button>
                      {renderSaidaParaGratuito(
                        'Quero voltar a usar o plano gratuito',
                        AVISO_PERDA_DE_ACESSO_PAGO,
                      )}
                    </>
                );
                if (isTrialExpired) return (
                    <>
                      <Button
                        className="w-full py-6 text-base font-semibold"
                        onClick={() => navigate('/account?tab=plan')}
                      >
                        <Sparkles className="h-5 w-5 mr-2" />
                        Visualizar Planos
                        <ArrowRight className="h-5 w-5 ml-2" />
                      </Button>
                      {renderSaidaParaGratuito('Continuar no plano gratuito', AVISO_PERDA_DE_ACESSO_PAGO)}
                    </>
                );
                return (
                    <Button
                      className="w-full py-6 text-base font-semibold"
                      onClick={() => navigate('/account?tab=plan')}
                    >
                      <Sparkles className="h-5 w-5 mr-2" />
                      Escolher um Plano
                      <ArrowRight className="h-5 w-5 ml-2" />
                    </Button>
                );
              })()}

              <Button
                variant="ghost"
                className="w-full text-gray-500 hover:text-gray-700"
                onClick={logout}
              >
                <LogOut className="h-4 w-4 mr-2" />
                Sair da conta
              </Button>
            </div>

            {/* Help Text */}
            <div className="text-center pt-4 border-t border-border">
              <p className="text-sm text-muted-foreground">
                Precisa de ajuda?{' '}
                <button 
                  onClick={() => navigate('/support')}
                  className="text-primary hover:underline font-medium"
                >
                  Entre em contato com o suporte
                </button>
              </p>
            </div>
          </CardContent>
        </Card>

        {/* Additional Info */}
        <p className="text-center text-xs text-muted-foreground">
          Suas informações financeiras estão seguras e serão preservadas.
        </p>
      </div>
    </div>
  );
}

export default SubscriptionBlockedPage;

